import { ChangeEvent, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { SaveOutlined, TuneOutlined } from '@mui/icons-material';
import TenantAutocomplete, { TenantShortDto } from '../../components/common/TenantAutocomplete';
import { medicalCheckupsService } from '../../services/medicalCheckupsService';
import { TenantQueueSettingDto } from '../../models/medicalCheckups/tenantQueueSettingDto';

const getError = (error: any) => error?.response?.data?.message || error?.message || 'Не удалось сохранить настройки очереди';

export const MedicalCheckupQueueSettingsPanel = () => {
  const [tenantName, setTenantName] = useState<string>('');
  const [fallbackTenant, setFallbackTenant] = useState<TenantShortDto | null>(null);
  const [settings, setSettings] = useState<TenantQueueSettingDto>({
    tenantId: 0,
    enabled: false,
    offerTimeoutSeconds: 30,
    primaryOrganizationSlaSeconds: 600,
    fallbackTenantId: null,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError('');

    medicalCheckupsService.getTenantQueueSettings()
      .then(({ data }) => {
        setSettings(data.data);
        setTenantName('');
      })
      .catch((e) => setError(getError(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!settings.fallbackTenantId) {
      setFallbackTenant(null);
    }
  }, [settings.fallbackTenantId]);

  const updateNumber = (field: 'offerTimeoutSeconds' | 'primaryOrganizationSlaSeconds') => (event: ChangeEvent<HTMLInputElement>) => {
    setSettings((current) => ({ ...current, [field]: Number(event.target.value) }));
  };

  const updateFallbackTenant = (tenant: TenantShortDto | null) => {
    setFallbackTenant(tenant);
    setSettings((current) => ({ ...current, fallbackTenantId: tenant?.id ?? null }));
  };

  const save = async () => {
    setSaving(true);
    setError('');
    setSuccess(false);

    try {
      const response = await medicalCheckupsService.updateTenantQueueSettings({
        offerTimeoutSeconds: settings.offerTimeoutSeconds,
        primaryOrganizationSlaSeconds: settings.primaryOrganizationSlaSeconds,
        fallbackTenantId: fallbackTenant?.id ?? null,
        enabled: settings.enabled,
      });
      setSettings(response.data.data);
      setSuccess(true);
    } catch (e) {
      setError(getError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ maxWidth: 920, p: { xs: 2, sm: 3 }, mt: 3 }}>
      <Stack spacing={3}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <TuneOutlined color="primary" />
            <Typography variant="h6">Очередь медосмотров</Typography>
          </Stack>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            Настройки определяют, сколько времени медик видит заявку и когда осмотр уходит в резервную медорганизацию.
          </Typography>
        </Box>

        {loading ? (
          <Box py={5} textAlign="center"><CircularProgress /></Box>
        ) : (
          <Stack spacing={2.5}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.enabled}
                  onChange={(_, value) => setSettings((current) => ({ ...current, enabled: value }))}
                />
              }
              label={settings.enabled ? 'Переброс включён' : 'Переброс выключен'}
            />

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Время на медика, секунд"
                type="number"
                value={settings.offerTimeoutSeconds}
                onChange={updateNumber('offerTimeoutSeconds')}
                inputProps={{ min: 5, max: 300 }}
                helperText="Например 30 секунд"
                fullWidth
              />
              <TextField
                label="До резерва, секунд"
                type="number"
                value={settings.primaryOrganizationSlaSeconds}
                onChange={updateNumber('primaryOrganizationSlaSeconds')}
                inputProps={{ min: 60, max: 3600 }}
                helperText="600 секунд = 10 минут"
                fullWidth
              />
            </Stack>

            <TenantAutocomplete
              tenant={fallbackTenant}
              tenantId={settings.fallbackTenantId}
              onTenantChange={updateFallbackTenant}
              label="Резервная медорганизация"
              style={{ width: '100%' }}
            />

            {error && <Alert severity="error">{error}</Alert>}
            {success && <Alert severity="success">Настройки очереди сохранены</Alert>}

            <Box>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveOutlined />}
                disabled={saving}
                onClick={save}
              >
                Сохранить настройки очереди
              </Button>
            </Box>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};
